import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, query, collection } from "firebase/firestore";
import { auth, db } from "../firebase";
import { translations } from "../utils/dictionary";
import { Pet } from "../utils/helpers";

// 1. Описуємо, які саме дані будуть лежати в нашій "хмарі"
interface AppContextType {
  lang: "uk" | "en" | null;
  setLang: (lang: "uk" | "en") => void;
  t: typeof translations.uk;
  isDark: boolean;
  setIsDark: (dark: boolean) => void;
  user: FirebaseUser | null;
  loading: boolean;
  pets: Pet[];
  activePetId: string | number | null;
  setActivePetId: (id: string | number | null) => void;
  activePet: Pet;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// 2. Створюємо Провайдер (Обгортку для нашого додатку)
export const AppProvider = ({ children }: { children: ReactNode }) => {
  // --- МОВА ТА ТЕМА ---
  const [lang, setLangState] = useState<"uk" | "en" | null>(() => localStorage.getItem("mypaws_lang") as "uk" | "en" | null);
  const t = lang ? translations[lang] : translations.uk;
  const [isDark, setIsDarkState] = useState(() => localStorage.getItem("mypaws_theme") === "dark");

  const setLang = (newLang: "uk" | "en") => { setLangState(newLang); localStorage.setItem("mypaws_lang", newLang); };
  const setIsDark = (dark: boolean) => { setIsDarkState(dark); localStorage.setItem("mypaws_theme", dark ? "dark" : "light"); };

  // --- КОРИСТУВАЧ ---
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // --- ПЕСИКИ ---
  const [pets, setPets] = useState<Pet[]>([]);
  const [activePetId, setActivePetId] = useState<string | number | null>(null);
  const activePet = pets.find((p) => p.id === activePetId) || pets[0] || { name: t.myPet, species: t.dog, breed: "", gender: "", id: "", photo: null, city: "", diagnoses: [], meds: [], allergies: [] };

  // --- СИСТЕМНІ ЕФЕКТИ ---
  // Слідкуємо за входом/виходом користувача
  useEffect(() => {
    if (!lang) return;
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission();
    
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        const userRef = doc(db, "users", u.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          await setDoc(userRef, { uid: u.uid, email: u.email, createdAt: serverTimestamp(), lastLogin: serverTimestamp() });
        } else {
          await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
        }
      }
    });
    return () => unsub();
  }, [lang]);

  // Слідкуємо за списком песиків у базі
  useEffect(() => {
    if (!user) return;
    const unsubPets = onSnapshot(query(collection(db, "users", user.uid, "pets")), (snap) => {
      const pList = snap.docs.map(d => d.data() as Pet);
      setPets(pList);
      if (pList.length > 0 && !activePetId) setActivePetId(pList[0].id);
      else if (pList.length === 0) {
        const newId = Date.now().toString();
        const defPet: Pet = { id: newId, userId: user.uid, name: t.myPet, species: t.dog, breed: "", gender: "", dob: "", chip: "", weight: "", features: "", photo: null, city: "", diagnoses: [], meds: [], allergies: [] };
        setDoc(doc(db, "users", user.uid, "pets", newId), defPet).catch(console.error);
        setActivePetId(newId);
      }
    });
    return () => unsubPets();
  }, [user, activePetId, t.myPet, t.dog]);

  return (
    <AppContext.Provider value={{ lang, setLang, t, isDark, setIsDark, user, loading, pets, activePetId, setActivePetId, activePet }}>
      {children}
    </AppContext.Provider>
  );
};

// 3. Хук для швидкого доступу до даних з будь-якого файлу
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used within AppProvider");
  return context;
};